import {Injectable} from '@angular/core';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {CommonService} from '../../../services/common.service';
import {UserConfigurationMetaData} from '../../models/user/users-query-result';
import {UserService} from '../../services/user.service';
import {SharedState} from '../shared/shared.state';
import {
  ChangeUserViewType,
  GetUsersConfiguration,
  IsPinCodeAlreadyInUse,
  LoadIdHoldersName,
  LoadMoreUsersResults,
  LoadUsers,
  SaveOrModifyUser,
  SaveUserPermissions,
  SelectUser,
  SetIsLoadingData,
  SetUsersFilter,
  UpdateUserAccessPointsState
} from './users.actions';
import {UsersStateModel} from './users.model';
import {MonitoringService} from '../../../services/logging.service';

@State<UsersStateModel>({
  name: 'users',
  defaults: {
    idHoldersName: null,
    users: null,
    totalUsersCount: 0,
    currentPage: 0,
    selectedUser: null,
    displayType: 1,
    isLoadingData: false,
    userFilter: {
      search: null,
      filter: {
        type: null,
        department: null,
        accessGroup: null,
        accessLevels: null,
        carParkingGroup: null,
        cardAndCardGroups: null,
        status: true
      }
    },
    configuration: null,
    isTouched: false,
    permissions: null
  }
})
@Injectable()
export class UsersState {
  constructor(
    private readonly userService: UserService,
    private store: Store,
    private commonService: CommonService,
    private monitoringService: MonitoringService
  ) {
  }

  @Action(LoadUsers)
  async loadUsers(ctx: StateContext<UsersStateModel>) {
    ctx.patchState({isLoadingData: true});
    const chunkSize = this.store.selectSnapshot(SharedState.resultsChunkSize);
    let users = await this.userService.getUsers(1, chunkSize, ctx.getState().userFilter);
    if (users?.results) {
      users.results = this.userImageRoutine(users.results);
      if (users.results) {
        users.results.sort(this.commonService.compareByFirstAndLastNameAscFn);
      }
      ctx.patchState({
        users: users.results,
        totalUsersCount: users.totalCount,
        currentPage: 1,
        isTouched: true,
        isLoadingData: false
      });
    } else {
      ctx.patchState({isLoadingData: false});
    }
  }

  @Action(LoadIdHoldersName)
  async loadIdHoldersName(ctx: StateContext<UsersStateModel>) {
    let idHoldersName = await this.userService.getIdHolders();
    if (idHoldersName) {
      ctx.patchState({idHoldersName});
    }
  }

  @Action(LoadMoreUsersResults)
  async loadMoreResults(ctx: StateContext<UsersStateModel>, action: LoadMoreUsersResults) {
    if (action.resetChunk) {
      ctx.patchState({currentPage: 0});
    }
    let {currentPage, users} = ctx.getState();
    const chunkSize = this.store.selectSnapshot(SharedState.resultsChunkSize);
    let nextPage: number = currentPage;
    ctx.patchState({isLoadingData: true});
    let usersResults = await this.userService.getUsers(++nextPage, chunkSize, ctx.getState().userFilter);

    if (usersResults?.results) {

      usersResults.results = this.userImageRoutine(usersResults.results);
      ctx.patchState({isLoadingData: false});
      const usersList = action.resetChunk ? [] : users;
      const combinedUsersList = [...usersList, ...usersResults.results];
      let reducedUsersList = combinedUsersList.reduce((acc, value) => acc.some(i => i.id === value.id) ? acc : acc.concat(value), []);
      if (reducedUsersList) {
        reducedUsersList.sort(this.commonService.compareByFirstAndLastNameAscFn);
      }
      ctx.patchState({
        isTouched: true,
        users: reducedUsersList,
        totalUsersCount: usersResults.totalCount
      });
      if (usersList.length !== ctx.getState().users.length) {
        ctx.patchState({
          currentPage: nextPage
        });
      }
    }
  }

  @Action(SelectUser)
  async selectUser(ctx: StateContext<UsersStateModel>, action: SelectUser) {
    if (action.user && action.user.id) {
      ctx.patchState({isLoadingData: true});
      this.userService.getUserDetailsById(action.user.id).then(u => {
        if (u) {
          const {user} = u;
          if (user) {
            if (user.validUntil === 0) {
              user.validUntil = null;
              user.isValidUntil = false;
            } else {
              user.isValidUntil = true;
            }
            if (user.imageBase64) {
              user.imageBase64 = this.commonService.sanitizeImage(user.imageBase64);
            }
            if (user.photo) {
              user.photo = this.commonService.sanitizeImage(user.photo);
            }
            user.credentials.forEach(cred => {
              cred.isReadonly = true;
            });
            if (user.pinCode) {
              user.credentials.push(
                {
                  facilityCode: '',
                  cardCode: user.pinCode,
                  id: 'pin',
                  isActive: true,
                  isReadonly: true,
                  type: 'pin',
                  typeId: 1,
                  wiegandTypeId: 1
                }
              );
            }

            //convert Server object  additionalReaders to front
            user.additionalReaders = [...user.additionalReaders].map(item => {
              return {
                iD: item['reader'].iD,
                tDesc: item['reader'].tDesc,
                bReaderOut: item['reader'].bReaderOut,
                idTimezone: item['timeZone'].iD,
                networkId: item['reader'].networkId,
                networkDesc: item['reader'].networkDesc
              };
            });

            ctx.patchState({selectedUser: u.user});
            const {users} = ctx.getState();
            let userInList = users.filter(user => user.id === u.user.id);
            if (userInList.length > 0) {
              const indexOf = users.indexOf(userInList[0]);
              users[indexOf] = {...u.user};
              ctx.patchState({users: [...users]});
            }
            ctx.patchState({isLoadingData: false});
          } else {
            ctx.patchState({isLoadingData: false, selectedUser: null});
          }
        }
      });
    } else {
      ctx.patchState({selectedUser: null});
    }
  }

  @Action(ChangeUserViewType)
  async changeUserViewType(ctx: StateContext<UsersStateModel>, action: ChangeUserViewType) {
    ctx.patchState({displayType: action.displayType});
  }

  @Action(SetUsersFilter)
  async setUsersFilter(ctx: StateContext<UsersStateModel>, action: SetUsersFilter) {
    ctx.patchState({userFilter: {...action.userFilter}});
  }

  @Action(GetUsersConfiguration)
  async getUsersConfiguration(ctx: StateContext<UsersStateModel>) {
    ctx.patchState({isLoadingData: true});
    const configuration: UserConfigurationMetaData = await this.userService.getConfiguration();
    if (configuration) {
      this.prepareConfigurations(configuration);
      ctx.patchState({isLoadingData: false});
      ctx.patchState({configuration});
    } else {
      setTimeout(async () => {
        const configuration: UserConfigurationMetaData = await this.userService.getConfiguration();
        if (configuration) {
          this.prepareConfigurations(configuration);
          ctx.patchState({isLoadingData: false});
          ctx.patchState({configuration});
        }
      }, 3000);
    }
  }

  private prepareConfigurations(configuration: UserConfigurationMetaData):void {
    configuration.carParkingGroup?.unshift({id: 0, description: 'None'});
    configuration.cardAndCardGroups?.unshift({id: 0, description: 'None'});
    configuration.outputGroups?.unshift({id: 0, description: 'None'});
    configuration.accessAreas?.unshift({id: 0, description: 'None'});
  }

  @Action(SaveOrModifyUser)
  async saveOrModifyUser(ctx: StateContext<UsersStateModel>, action: SaveOrModifyUser) {
    try {
      const saveUserResult = await this.userService.saveOrModifyUser(action.user);
      if (saveUserResult.add) {
        action.saveUserCallback({userId: saveUserResult.add}, action.closeFormAfter);
      } else if (saveUserResult.update) {
        action.saveUserCallback({userId: action.user.id}, action.closeFormAfter);
      } else {
        this.monitoringService.logException(new Error('Unable to save user'));
      }
    } catch (err) {
      this.monitoringService.logException(err);
      action.saveUserCallback({errors: err}, action.closeFormAfter);
    }
  }

  @Action(UpdateUserAccessPointsState)
  async updateUserAccessPointsState(ctx: StateContext<UsersStateModel>, action: UpdateUserAccessPointsState): Promise<any> {
    const {selectedUser} = ctx.getState();
    if (selectedUser.doors.results.length > 0) {
      const doors = {...selectedUser.doors};
      action.payload.accessPoints.forEach(accessPoint => {
        const selectedAccessPointIndex = doors.results.findIndex(door => door.id === accessPoint.id);
        if (selectedAccessPointIndex !== -1) {
          doors.results[selectedAccessPointIndex] = {
            ...doors.results[selectedAccessPointIndex],
            isOpen: accessPoint.isOpen,
            isSecured: accessPoint.isSecured,
            isLockdown: accessPoint.isLockdown
          };
        }
      });
      ctx.patchState({selectedUser: {...selectedUser, doors: {...doors}}});
    }
  }

  @Action(SaveUserPermissions)
  async saveUserPermissionsState(ctx: StateContext<UsersStateModel>, action: SaveUserPermissions): Promise<any> {
    ctx.patchState({permissions: {action}});
  }

  private userImageRoutine(users) {
    return users.map(user => {
      if (user.imageBase64) {
        user.imageBase64 = this.commonService.sanitizeImage(user.imageBase64);
      }
      if (user.photo) {
        user.photo = this.commonService.sanitizeImage(user.photo);
      }
      return user;
    });
  }

  @Action(IsPinCodeAlreadyInUse)
  async checkIsPinCodeAlreadyInUse(ctx: StateContext<IsPinCodeAlreadyInUse>, action: IsPinCodeAlreadyInUse) {
    try {
      const isPinCodeAlreadyInUseResult = await this.userService.isPinCodeAlreadyInUse(action.user, action.pinCode);
      if (isPinCodeAlreadyInUseResult.isPinCodeAlreadyInUseAsync) {
        action.isPinCodeAlreadyInUseCallback({isPinCodeAlreadyInUseAsync: isPinCodeAlreadyInUseResult.isPinCodeAlreadyInUseAsync});
      }
    } catch (err) {
      this.monitoringService.logException(err);
      action.isPinCodeAlreadyInUseCallback({errors: err});
    }
  }

  @Action(SetIsLoadingData)
  async setIsLoadingData(ctx: StateContext<UsersStateModel>, action: SetIsLoadingData) {
    ctx.patchState({isLoadingData: action.isLoadingData});
  }
}
